"use client"

import type React from "react"
import { useRef, useState } from "react"
import { QRCodeCanvas } from "qrcode.react"
import { Box, Button, CircularProgress, Grid } from "@mui/material"
import { GREEN_LOGO, GREEN_MAIN, WHITE } from "../../theme/colors"
import { useTranslationContext } from "../../hooks/translations/translationsProvider"

type QRCodeGeneratorProps = {
  value: string
  logoUrl?: string // URL logo centrale (opzionale)
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({ value, logoUrl }) => {
  const qrRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const {t} = useTranslationContext();

  const handleDownload = async () => {
    if (!qrRef.current) return

    setIsDownloading(true)

    try {
      // Get the QRCodeCanvas element
      const qrCanvas = qrRef.current.querySelector("canvas")

      if (!qrCanvas || !canvasRef.current) {
        console.error("Canvas not found")
        setIsDownloading(false)
        return
      }

      // Get the 2D context of our canvas
      const ctx = canvasRef.current.getContext("2d")
      if (!ctx) {
        console.error("Could not get 2D context")
        setIsDownloading(false)
        return
      }

      // Set canvas dimensions
      const canvasWidth = 1000
      const canvasHeight = 1200
      canvasRef.current.width = canvasWidth
      canvasRef.current.height = canvasHeight

      // Fill background
      ctx.fillStyle = "white"
      ctx.fillRect(0, 0, canvasWidth, canvasHeight)

      // Calculate QR code size (90% of width)
      const qrSize = Math.round(canvasWidth * 0.9)
      const qrX = (canvasWidth - qrSize) / 2
      const qrY = (canvasHeight - qrSize - 100) / 2 // Leave space for text

      // Draw QR code
      const img = new Image()
      img.crossOrigin = "anonymous" // Important for CORS

      // Create a temporary canvas to get the QR code data
      const tempCanvas = document.createElement("canvas")
      tempCanvas.width = qrCanvas.width
      tempCanvas.height = qrCanvas.height
      const tempCtx = tempCanvas.getContext("2d")
      if (tempCtx) {
        tempCtx.drawImage(qrCanvas, 0, 0)

        // Use the data URL as source
        img.src = tempCanvas.toDataURL("image/png")

        img.onload = () => {
          // Draw QR code centered and at 90% width
          ctx.drawImage(img, qrX, qrY, qrSize, qrSize)

          // Draw text
          ctx.textAlign = "center"

          // Handle text wrapping if needed
          const maxWidth = canvasWidth * 0.9
          ctx.font = '30px Poppins';
          ctx.fillStyle = GREEN_MAIN; 
          const text = value
          ctx.fillText(text, canvasWidth / 2, qrY + qrSize + 50)

          // Convert to data URL and download
          const dataUrl = canvasRef.current!.toDataURL("image/png")
          const link = document.createElement("a")
          link.download = "qrcode.png"
          link.href = dataUrl
          link.click()

          setIsDownloading(false)
        }

        img.onerror = () => {
          console.error("Error loading QR code image")
          setIsDownloading(false)
        }
      } else {
        setIsDownloading(false)
      }
    } catch (error) {
      console.error("Errore nel download del QR code:", error)
      setIsDownloading(false)
    }
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      {/* Visible QR code */}
      <Box
        ref={qrRef}
        sx={{
          p: 4,
          backgroundColor: "white",
          borderRadius: 2,
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <QRCodeCanvas
          value={value}
          size={300}
          level="H"
          includeMargin
          fgColor={GREEN_MAIN}
          style={{
            maxWidth: "100%",
            height: "auto",
          }}
          imageSettings={
            logoUrl
              ? {
                  src: logoUrl,
                  width: 120,
                  height: 120,
                  excavate: true,
                }
              : undefined
          }
        />
      </Box>

      {/* Hidden canvas for generating the download image */}
      <canvas ref={canvasRef} style={{ display: "none" }} width="1000" height="1200" />

        <Grid container justifyContent="center" alignItems="center" spacing={0}>
            <Grid item xs='auto'>
                <Button
                    variant="contained"
                    color="info"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    sx={{
                        margin:"10px 0 0 0",
                        height: '35px',
                        color:WHITE,
                        backgroundColor: GREEN_LOGO,
                        "&:hover": { backgroundColor: "darkgreen" },
                        border: "1px solid " + GREEN_LOGO,
                    }}
                >
                    {isDownloading ? (
                    <>
                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                       {t("Generazione in corso...")}
                    </>
                    ) : (
                    t("Scarica QrCode")
                    )}
                </Button>
            </Grid>
        </Grid>
    </Box>
  )
}

export default QRCodeGenerator
